import {
  type CohortBuilderDefinitionLegacy,
  SubjectVariableCriteriaSelectionTypeLegacy,
} from '@verantos-dev/types-cohorts';

import { getEventNameFromGrammarValue } from './getEventNameFromGrammarValue';
import {
  OBSERVATION_PERIOD_BASELINE_RESTRICTION,
  OBSERVATION_PERIOD_FOLLOW_UP_RESTRICTION,
} from '../constants/constants';

export const getInUseCohortBuilderItemsMap = (
  cohortDefinition: CohortBuilderDefinitionLegacy | undefined,
  cohortBuilderItemNamesToIdsMap: Record<string, string>
) => {
  const inUseCohortBuilderItemsMap: Record<string, string[]> = {};
  const indexDateId = cohortDefinition?.indexDate?.id;

  cohortDefinition?.subjectVariables.forEach((subjectVariable) => {
    // Set subject variable to in use if criteria selection is not off
    if (
      subjectVariable.criteriaSelection !== SubjectVariableCriteriaSelectionTypeLegacy.NOT_APPLIED
    ) {
      inUseCohortBuilderItemsMap[subjectVariable.id] = inUseCohortBuilderItemsMap[
        subjectVariable.id
      ]
        ? [...inUseCohortBuilderItemsMap[subjectVariable.id], subjectVariable.criteriaSelection]
        : [subjectVariable.criteriaSelection];
    }

    // set index date if it is in use in a subject variable
    if (indexDateId) {
      subjectVariable.numericFilters.forEach((filter) => {
        filter.inputFields.forEach((inputField) => {
          if (['AGE_AT_INDEX', 'Age at index'].includes(inputField.value || '')) {
            inUseCohortBuilderItemsMap[indexDateId] = inUseCohortBuilderItemsMap[indexDateId]
              ? [...inUseCohortBuilderItemsMap[indexDateId], subjectVariable.id]
              : [subjectVariable.id];
          }
        });
      });
    }

    // derived events and simple events
    // The first input field in a count filter is the event name
    if (subjectVariable.countFilter?.inputFields[0]?.value) {
      const referencedEventName = getEventNameFromGrammarValue(
        subjectVariable.countFilter.inputFields[0].value
      );
      const inUseEventId =
        referencedEventName && cohortBuilderItemNamesToIdsMap[referencedEventName];
      if (inUseEventId) {
        inUseCohortBuilderItemsMap[inUseEventId] = inUseCohortBuilderItemsMap[inUseEventId]
          ? [...inUseCohortBuilderItemsMap[inUseEventId], subjectVariable.id]
          : [subjectVariable.id];
      }
    }
  });

  // Handle setting index date in use cases for observation period restrictions
  if (indexDateId) {
    const baselinePeriod = cohortDefinition?.studyPeriodRestrictions?.baselinePeriodRestriction;
    if (baselinePeriod?.isEnabled) {
      // Set index date to in use if baseline period is enabled
      inUseCohortBuilderItemsMap[indexDateId] = inUseCohortBuilderItemsMap[indexDateId]
        ? [...inUseCohortBuilderItemsMap[indexDateId], OBSERVATION_PERIOD_BASELINE_RESTRICTION]
        : [OBSERVATION_PERIOD_BASELINE_RESTRICTION];
    }

    const followUpPeriod = cohortDefinition?.studyPeriodRestrictions?.followUpPeriodRestriction;
    if (followUpPeriod?.isEnabled) {
      // Set index date to in use if follow up period is enabled
      inUseCohortBuilderItemsMap[indexDateId] = inUseCohortBuilderItemsMap[indexDateId]
        ? [...inUseCohortBuilderItemsMap[indexDateId], OBSERVATION_PERIOD_FOLLOW_UP_RESTRICTION]
        : [OBSERVATION_PERIOD_FOLLOW_UP_RESTRICTION];
    }
  }

  // Get the current in use item for the index date
  const indexDateValue = cohortDefinition?.indexDate?.indexDateValue;
  const referencedEventName = indexDateValue && getEventNameFromGrammarValue(indexDateValue);
  const inUseEventId = referencedEventName && cohortBuilderItemNamesToIdsMap[referencedEventName];
  if (inUseEventId && indexDateId) {
    inUseCohortBuilderItemsMap[inUseEventId] = inUseCohortBuilderItemsMap[inUseEventId]
      ? [...inUseCohortBuilderItemsMap[inUseEventId], indexDateId]
      : [indexDateId];
  }

  // Get the current in use derived events
  cohortDefinition?.derivedEvents.forEach(({ id, eventFilters }) => {
    // Get in use derived events and simple events from event filters
    eventFilters.forEach(({ eventName }) => {
      const referencedEventName = getEventNameFromGrammarValue(eventName.value || '');
      const inUseEventId =
        referencedEventName && cohortBuilderItemNamesToIdsMap[referencedEventName];
      if (inUseEventId) {
        inUseCohortBuilderItemsMap[inUseEventId] = inUseCohortBuilderItemsMap[inUseEventId]
          ? [...inUseCohortBuilderItemsMap[inUseEventId], id]
          : [id];
      }
    });
  });

  return inUseCohortBuilderItemsMap;
};
